import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { toast } from 'react-toastify';
import AfasService from 'Api/Afas';
import {
    AFAS_SUB_TAB_KEYS,
    AFAS_VERZOEK_LEFT_FIELDS,
    AFAS_VERZOEK_RIGHT_FIELDS,
    createDefaultLangeVerzoekDays,
    isLangeVerzoekenWeek,
} from '../constants';
import { useAfasContext } from '../context/AfasContext';
import { mapOpenstaandSidebarRows } from '../utils/mapOpenstaandSidebar';
import { mergeVerzoekFieldOptions } from '../utils/mergeVerzoekFieldOptions';
import {
    applyDeclarerenFinancieringsbron,
    filterFinancieringsbronOptions,
    syncFinancieringsbronForDeclareren,
} from '../utils/declarerenFinancieringsbron';
import { hydrateExtraValues } from '../utils/extraFieldOptions';
import AfasVerzoekFields from '../components/AfasVerzoekFields';
import AfasFilterSidebar from '../components/AfasFilterSidebar';
import AfasSubTabBar from '../components/AfasSubTabBar';
import AfasDagdelenTable from '../components/AfasDagdelenTable';
import AfasLangeVerzoekenPanel from '../components/AfasLangeVerzoekenPanel';
import AfasExtraPanel from '../components/AfasExtraPanel';
import AfasOpmerkingMemoPanel from '../components/AfasOpmerkingMemoPanel';
import AfasFooterActions from '../components/AfasFooterActions';
import AfasListToolbar from '../components/AfasListToolbar';
import AfasDagdelenEditModal from '../components/AfasDagdelenEditModal';
import AfasVerzendenValidationModal from '../components/AfasVerzendenValidationModal';
import AfasQuestion1Mask from '../components/AfasQuestion1Mask';
import { aggregateDagdelenByDate } from '../utils/aggregateDagdelenByDate';
import { validateOpenstaandVerzenden } from '../utils/validateOpenstaandVerzenden';
import Loader from "react-loaders";

const Openstaand = () => {
    const [activeSubTab, setActiveSubTab] = useState(AFAS_SUB_TAB_KEYS.DAGDELEN);
    const [listMode, setListMode] = useState('auto');
    const [submitMode, setSubmitMode] = useState('auto');
    const [selectedWeek, setSelectedWeek] = useState('');
    const [dagtypeFilter, setDagtypeFilter] = useState('');
    const [selectedSubstitutorAdminId, setSelectedSubstitutorAdminId] = useState(null);
    const [selectedProlongedId, setSelectedProlongedId] = useState(null);
    const [verzoekDraft, setVerzoekDraft] = useState({});
    const [extraDraft, setExtraDraft] = useState({});
    const [langeBeginDatum, setLangeBeginDatum] = useState('');
    const [langeEindDatum, setLangeEindDatum] = useState('');
    const [langeDays, setLangeDays] = useState(() => createDefaultLangeVerzoekDays());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingDagdeel, setEditingDagdeel] = useState(null);
    const [verzendenValidationMessage, setVerzendenValidationMessage] = useState(null);
    /** Bluebox: after manual Volgnummer select, Extra school panel is filled and mask lifted. */
    const [question1ManualOverride, setQuestion1ManualOverride] = useState(false);
    const lastLangeHydrateKeyRef = useRef('');
    const extraFromApiRef = useRef(false);

    const {
        communityId,
        selectedSubstitutionId,
        setSelectedSubstitutionId,
        refreshTabCounts,
    } = useAfasContext();
    const navigate = useNavigate();

    // Clear the open verzoek in the same render as an organisatie/week change.
    // A later effect is too late: SWR would request the previous bestuur's verzoek
    // with the new community id, get 404, and the API client would log the user out.
    const [selectionScope, setSelectionScope] = useState({
        communityId,
        week: selectedWeek,
    });
    const communityChanged = selectionScope.communityId !== communityId;
    const weekChanged = selectionScope.week !== selectedWeek;
    const scopeChanged = communityChanged || weekChanged;
    const requestSubstitutionId = scopeChanged ? null : selectedSubstitutionId;
    const requestSubstitutorId = scopeChanged ? null : selectedSubstitutorAdminId;
    const requestProlongedId = scopeChanged ? null : selectedProlongedId;

    if (scopeChanged) {
        setSelectionScope({
            communityId,
            week: selectedWeek,
        });
        setSelectedSubstitutorAdminId(null);
        setSelectedProlongedId(null);
        setSelectedSubstitutionId(null);
        if (communityChanged) {
            setDagtypeFilter('');
        }
    }

    // Bluebox: week 999999 → only Langdurige tab; otherwise only Dagdelen
    useEffect(() => {
        if (isLangeVerzoekenWeek(selectedWeek)) {
            setActiveSubTab(AFAS_SUB_TAB_KEYS.LANGE_VERZOEKEN);
            return;
        }

        setActiveSubTab((current) =>
            current === AFAS_SUB_TAB_KEYS.LANGE_VERZOEKEN
                ? AFAS_SUB_TAB_KEYS.DAGDELEN
                : current,
        );
    }, [selectedWeek]);

    const listKey = communityId
        ? ['afas-openstaand-list', communityId, listMode, selectedWeek]
        : null;

    const { data: listResponse, isLoading: listLoading, mutate: refreshList } = useSWR(
        listKey,
        () =>
            AfasService.getOpenstaandVerzoeken(communityId, {
                week: selectedWeek || null,
                mode: listMode,
            }).then((res) => res.data),
    );

    const sidebarRows = useMemo(() => {
        let rows = mapOpenstaandSidebarRows(listResponse?.data?.groups ?? []);

        if (dagtypeFilter) {
            rows = rows.filter((row) => String(row.dagtype) === String(dagtypeFilter));
        }

        return rows;
    }, [listResponse, dagtypeFilter]);

    const dagtypeOptions = useMemo(() => {
        const values = new Set();
        mapOpenstaandSidebarRows(listResponse?.data?.groups ?? []).forEach((row) => {
            if (row.dagtype && row.dagtype !== '—') {
                values.add(String(row.dagtype));
            }
        });
        return Array.from(values)
            .sort((a, b) => a.localeCompare(b, 'nl'))
            .map((value) => ({ value, label: value }));
    }, [listResponse]);

    useEffect(() => {
        if (!selectedSubstitutionId && sidebarRows.length > 0) {
            const first = sidebarRows[0];
            if (first?.substitutionId) {
                setSelectedSubstitutionId(first.substitutionId);
                setSelectedSubstitutorAdminId(first.substitutorAdminId ?? null);
                setSelectedProlongedId(first.prolongedId ?? null);
            }
        }
    }, [sidebarRows, selectedSubstitutionId, setSelectedSubstitutionId]);

    const detailKey =
        communityId && requestSubstitutionId && requestSubstitutorId
            ? [
                'afas-openstaand-detail',
                communityId,
                requestSubstitutionId,
                requestSubstitutorId,
                selectedWeek,
                requestProlongedId,
            ]
            : null;

    const {
        data: detailResponse,
        isLoading: detailLoading,
        mutate: refreshDetail,
    } = useSWR(detailKey, () =>
        AfasService.getOpenstaandVerzoek(communityId, requestSubstitutionId, {
            substitutorAdminId: requestSubstitutorId,
            week: selectedWeek || null,
            prolongedId: requestProlongedId || null,
        }).then((res) => res.data),
    );

    const listItemsKey = communityId
        ? [
            'afas-openstaand-list-items',
            communityId,
            requestSubstitutorId,
            selectedWeek,
        ]
        : null;

    const { data: listItemsResponse } = useSWR(listItemsKey, () =>
        AfasService.getOpenstaandListItems(communityId, {
            substitutorAdminId: requestSubstitutorId,
            week: selectedWeek || null,
        }).then((res) => res.data),
    );

    const vervangtGuid = requestSubstitutionId
        ? String(verzoekDraft.vervangt || verzoekDraft.afwezigeMdwNr || '')
        : '';
    const isoPeriod = (value) => (/^\d{4}-\d{2}-\d{2}$/.test(String(value || '')) ? String(value) : '');
    const verzuimPeriodStart = isLangeVerzoekenWeek(selectedWeek) ? isoPeriod(langeBeginDatum) : '';
    const verzuimPeriodEnd = isLangeVerzoekenWeek(selectedWeek) ? isoPeriod(langeEindDatum) : '';
    const verzuimKey = communityId && vervangtGuid && vervangtGuid !== '-1'
        ? [
            'afas-verzuim-verlof',
            communityId,
            vervangtGuid,
            selectedWeek,
            verzuimPeriodStart,
            verzuimPeriodEnd,
        ]
        : null;

    const { data: verzuimResponse } = useSWR(verzuimKey, () =>
        AfasService.getOpenstaandVerzuimVerlof(communityId, {
            vervangt: vervangtGuid,
            week: selectedWeek || null,
            periodStart: verzuimPeriodStart || null,
            periodEnd: verzuimPeriodEnd || null,
        }).then((res) => res.data),
    );

    const fieldOptions = useMemo(() => {
        const fields = { ...(listItemsResponse?.data?.fields ?? {}) };
        const verzuimOptions = verzuimResponse?.data?.fields?.verzuimVerlofId;
        if (Array.isArray(verzuimOptions)) {
            fields.verzuimVerlofId = verzuimOptions;
        }
        return fields;
    }, [listItemsResponse?.data?.fields, verzuimResponse?.data?.fields?.verzuimVerlofId]);

    const verzoekLeftFields = useMemo(
        () => mergeVerzoekFieldOptions(AFAS_VERZOEK_LEFT_FIELDS, fieldOptions),
        [fieldOptions],
    );

    const verzoekRightFields = useMemo(
        () => mergeVerzoekFieldOptions(AFAS_VERZOEK_RIGHT_FIELDS, fieldOptions).map((field) => {
            if (field.id === 'financieringsbron') {
                const options = filterFinancieringsbronOptions(
                    field.options,
                    verzoekDraft.declarerenVf,
                );
                return Array.isArray(options) ? { ...field, options } : field;
            }
            if (field.id !== 'verzuimVerlofId') {
                return field;
            }
            const options = fieldOptions.verzuimVerlofId;
            if (Array.isArray(options) && options.length === 0) {
                return { ...field, readOnly: true };
            }
            return field;
        }),
        [fieldOptions, verzoekDraft.declarerenVf],
    );

    const verzendenOptions = useMemo(() => {
        const bron = fieldOptions.financieringsbron;
        if (!Array.isArray(bron)) {
            return fieldOptions;
        }

        return {
            ...fieldOptions,
            financieringsbron: filterFinancieringsbronOptions(bron, verzoekDraft.declarerenVf),
        };
    }, [fieldOptions, verzoekDraft.declarerenVf]);

    const verzendenCheck = useMemo(
        () => validateOpenstaandVerzenden(submitMode, verzoekDraft, verzendenOptions),
        [submitMode, verzoekDraft, verzendenOptions],
    );

    const extraFromVervangtRef = useRef(false);

    const applyExtraFromApi = useCallback((res) => {
        const payload = res.data?.data ?? res.data ?? {};
        const extra = payload.values ?? {};
        const verzoekPatch = payload.verzoek;
        extraFromVervangtRef.current = true;
        extraFromApiRef.current = true;
        setExtraDraft(hydrateExtraValues(extra, fieldOptions));
        if (verzoekPatch && typeof verzoekPatch === 'object') {
            setVerzoekDraft((previous) => ({
                ...previous,
                ...verzoekPatch,
            }));
        }
    }, [fieldOptions]);

    const handleVerzoekFieldChange = useCallback((fieldId, value) => {
        setVerzoekDraft((previous) => {
            const next = applyDeclarerenFinancieringsbron(previous, fieldId, value);
            if (fieldId === 'vervangt') {
                next.afwezigeMdwNr = value;
                next.vervangingsDvb = value ? 'yes' : 'no';
            }
            return next;
        });

        if (!communityId || !selectedSubstitutionId || !selectedSubstitutorAdminId) {
            return;
        }

        // Vervangt is the absent employee. Salary scale stays on the vervanger contract.
        if (fieldId === 'vervangt') {
            return;
        }

        // Bluebox onChangedDienstverband → reload Extra school panel + Extra tab
        if (fieldId !== 'volgnummerDvb') {
            return;
        }

        if (!value) {
            setQuestion1ManualOverride(false);
            return;
        }

        setQuestion1ManualOverride(true);
        AfasService.getOpenstaandExtra(communityId, selectedSubstitutionId, {
            substitutorAdminId: selectedSubstitutorAdminId,
            week: selectedWeek || null,
            volgnummerDvb: value,
            vervangt: verzoekDraft.vervangt || null,
        })
            .then(applyExtraFromApi)
            .catch(() => {
                toast.error('Contractgegevens voor dit volgnummer konden niet worden geladen.');
            });
    }, [
        applyExtraFromApi,
        communityId,
        selectedSubstitutionId,
        selectedSubstitutorAdminId,
        selectedWeek,
        verzoekDraft.vervangt,
        verzoekDraft.volgnummerDvb,
    ]);

    useEffect(() => {
        setQuestion1ManualOverride(false);
        extraFromApiRef.current = false;
    }, [detailResponse]);

    useEffect(() => {
        const verzoek = detailResponse?.data?.verzoek ?? {};
        const hasAbsentGuid = Boolean(verzoek.vervangt || verzoek.afwezigeMdwNr);

        setVerzoekDraft(syncFinancieringsbronForDeclareren({
            ...verzoek,
            // Legacy: Vervangt = AFAS medewerker van de afwezige (niet de vervanger uit de sidebar)
            vervangt: verzoek.vervangt || verzoek.afwezigeMdwNr || '',
            toonDagdelen: verzoek.toonDagdelen ?? 'yes',
            toonLangeContracten: verzoek.toonLangeContracten ?? 'no',
            contractTypeInfo: verzoek.contractTypeInfo ?? verzoek.contractType ?? '',
            vervangerUitPool: verzoek.vervangerUitPool ?? 'no',
            vervangingsDvb: verzoek.vervangingsDvb ?? (hasAbsentGuid ? 'yes' : 'no'),
        }));

        const prolonged = detailResponse?.data?.langeVerzoek ?? detailResponse?.data?.prolonged ?? null;
        if (prolonged?.prolongedId || prolonged?.id) {
            setSelectedProlongedId(Number(prolonged.prolongedId || prolonged.id));
        }
    }, [detailResponse]);

    useEffect(() => {
        extraFromVervangtRef.current = false;
    }, [detailResponse]);

    useEffect(() => {
        if (extraFromVervangtRef.current || extraFromApiRef.current) {
            setExtraDraft((previous) => hydrateExtraValues(previous, fieldOptions));
            return;
        }

        setExtraDraft(hydrateExtraValues(detailResponse?.data?.extra ?? {}, fieldOptions));
    }, [detailResponse, fieldOptions]);

    const langeHydrateKey = [
        selectedSubstitutionId ?? '',
        selectedProlongedId ?? '',
        selectedWeek ?? '',
    ].join(':');

    useEffect(() => {
        if (lastLangeHydrateKeyRef.current === langeHydrateKey) {
            return;
        }

        const prolonged =
            detailResponse?.data?.langeVerzoek ?? detailResponse?.data?.prolonged ?? null;
        const prolongedId = Number(prolonged?.prolongedId || prolonged?.id || 0);
        if (
            selectedProlongedId &&
            (prolongedId === 0 || prolongedId !== Number(selectedProlongedId))
        ) {
            return;
        }

        lastLangeHydrateKeyRef.current = langeHydrateKey;
        setLangeBeginDatum(prolonged?.beginDatum || prolonged?.startDate || '');
        setLangeEindDatum(prolonged?.eindDatum || prolonged?.endDate || '');
        if (Array.isArray(prolonged?.days) && prolonged.days.length > 0) {
            setLangeDays(prolonged.days);
        } else {
            setLangeDays(createDefaultLangeVerzoekDays());
        }
    }, [langeHydrateKey, detailResponse, selectedProlongedId]);

    const memo = detailResponse?.data?.memo ?? {};
    const dagdelen = detailResponse?.data?.dagdelen ?? [];
    const dagdelenByDate = useMemo(() => aggregateDagdelenByDate(dagdelen), [dagdelen]);
    const question1 = detailResponse?.data?.question1 ?? null;
    const question1Blocked = Boolean(
        question1 && question1.ok === false && !question1ManualOverride,
    );
    const question1MaskMessage = question1Blocked ? (question1.message || '') : '';
    const forceHandmatig = Boolean(question1?.forceHandmatig);

    // Bluebox: Question1 fail → disable Automatisch, force Handmatig
    useEffect(() => {
        if (!detailResponse?.data) {
            return;
        }
        if (forceHandmatig) {
            setSubmitMode('hand');
            return;
        }
        setSubmitMode((current) => (current === 'hand' ? 'auto' : current));
    }, [detailResponse, forceHandmatig]);

    const handleLangeWtfDaysChange = useCallback((nextDays) => {
        setLangeDays(nextDays);
    }, []);

    const handleLangeBeginDatumChange = useCallback((value) => {
        setLangeBeginDatum(value);
    }, []);

    const handleLangeEindDatumChange = useCallback((value) => {
        setLangeEindDatum(value);
    }, []);

    const handleDeleteDagdeel = async (dayRow) => {
        const shiftIds = (dayRow?.shifts ?? [])
            .map((shift) => shift.id)
            .filter((id) => id != null);
        if (!communityId || shiftIds.length === 0) {
            return;
        }

        const confirmLabel =
            shiftIds.length > 1
                ? `Alle dagdelen voor ${dayRow.datum || 'deze dag'} verwijderen?`
                : 'Dagdeel verwijderen?';
        if (!window.confirm(confirmLabel)) {
            return;
        }

        try {
            await Promise.all(
                shiftIds.map((id) => AfasService.deleteOpenstaandDagdeel(communityId, id)),
            );
            toast.success(shiftIds.length > 1 ? 'Dagdelen verwijderd' : 'Dagdeel verwijderd');
            refreshDetail();
            refreshList();
            refreshTabCounts?.();
        } catch {
            toast.error('Dagdeel kon niet worden verwijderd');
        }
    };

    const handleEditDagdeel = useCallback((row) => {
        setEditingDagdeel(row);
    }, []);

    const handleSaveDagdeelShifts = async (updates) => {
        if (!communityId || !Array.isArray(updates) || updates.length === 0) {
            return;
        }

        try {
            await Promise.all(
                updates.map((update) =>
                    AfasService.updateOpenstaandDagdeel(communityId, update.id, {
                        wtfValue: update.wtfValue,
                    }),
                ),
            );
            toast.success('WTF opgeslagen');
            setEditingDagdeel(null);
            refreshDetail();
            refreshList();
            refreshTabCounts?.();
        } catch (error) {
            const message =
                error?.response?.data?.message ?? 'WTF kon niet worden opgeslagen';
            toast.error(message);
        }
    };

    const handleSelectPerson = useCallback(
        (item) => {
            setSelectedSubstitutionId(item.substitutionId);
            setSelectedSubstitutorAdminId(item.substitutorAdminId ?? null);
            setSelectedProlongedId(item.prolongedId ?? null);
        },
        [setSelectedSubstitutionId],
    );

    const handleSaveMemo = async (salaryComments) => {
        if (!communityId || !selectedSubstitutionId) {
            return;
        }

        try {
            await AfasService.saveOpenstaandMemo(communityId, selectedSubstitutionId, {
                salaryComments,
            });
            toast.success('Memo opgeslagen');
            refreshDetail();
        } catch {
            toast.error('Memo kon niet worden opgeslagen');
        }
    };

    const handleVerwerken = async () => {
        if (!communityId || !submitMode) {
            return;
        }

        const isProlonged = Boolean(selectedProlongedId);
        if (
            !isProlonged &&
            (!selectedSubstitutionId || !selectedSubstitutorAdminId)
        ) {
            return;
        }

        const validation = validateOpenstaandVerzenden(
            submitMode,
            verzoekDraft,
            fieldOptions,
        );
        if (!validation.ok) {
            setVerzendenValidationMessage(validation.message);
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await AfasService.verwerkenOpenstaand(
                communityId,
                selectedSubstitutionId,
                {
                    substitutor_admin_id: selectedSubstitutorAdminId,
                    week: selectedWeek || null,
                    mode: submitMode,
                    list_mode: listMode,
                    verzoek: verzoekDraft,
                    extra: extraDraft,
                    prolonged_id: selectedProlongedId || null,
                    lange_verzoek: {
                        beginDatum: langeBeginDatum,
                        eindDatum: langeEindDatum,
                        days: langeDays,
                    },
                },
            );
            const data = response?.data?.data ?? {};
            const action = data.action;
            const message =
                data.message ??
                (action === 'moved_to_hand'
                    ? 'Verzoek toegevoegd aan de handmatige lijst'
                    : 'Verzoek in wachtrij geplaatst (Synchronisatie)');

            toast.success(message);

            if (action === 'queued_for_afas') {
                setSelectedSubstitutionId(null);
                setSelectedSubstitutorAdminId(null);
                setSelectedProlongedId(null);
                refreshList();
                refreshDetail();
                refreshTabCounts?.();
                navigate('/afas/synchronisatie');
                return;
            }

            if (action === 'moved_to_hand') {
                setListMode('hand');
                setSelectedSubstitutionId(null);
                setSelectedSubstitutorAdminId(null);
                setSelectedProlongedId(null);
                setSubmitMode('auto');
            } else if (action === 'afgemeld' || action === 'afgeboekt_als_gereed') {
                setSelectedSubstitutionId(null);
                setSelectedSubstitutorAdminId(null);
                setSelectedProlongedId(null);
                setSubmitMode('auto');
            }

            refreshList();
            refreshDetail();
            refreshTabCounts?.();
        } catch (error) {
            const message =
                error?.response?.data?.message ?? 'Verzenden mislukt';
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderSubTabContent = () => {
        switch (activeSubTab) {
            case AFAS_SUB_TAB_KEYS.DAGDELEN:
                return detailLoading ? (
                    <div className="afas-sub-tab-panel p-3"><Loader
                        type="ball-grid-pulse"
                        className="mt-3 d-flex mx-auto justify-content-center"
                    /></div>
                ) : (
                    <>
                        <AfasDagdelenTable
                            rows={dagdelenByDate}
                            onEdit={handleEditDagdeel}
                            onDelete={handleDeleteDagdeel}
                        />
                        <AfasDagdelenEditModal
                            isOpen={Boolean(editingDagdeel)}
                            dayRow={editingDagdeel}
                            onClose={() => setEditingDagdeel(null)}
                            onSave={handleSaveDagdeelShifts}
                        />
                    </>
                );
            case AFAS_SUB_TAB_KEYS.LANGE_VERZOEKEN:
                return (
                    <AfasLangeVerzoekenPanel
                        beginDatum={langeBeginDatum}
                        eindDatum={langeEindDatum}
                        days={langeDays}
                        onBeginDatumChange={handleLangeBeginDatumChange}
                        onEindDatumChange={handleLangeEindDatumChange}
                        onWtfDaysChange={handleLangeWtfDaysChange}
                        isLoading={detailLoading}
                        dagdeelgegevensRows={dagdelen}
                    />
                );
            case AFAS_SUB_TAB_KEYS.EXTRA:
                return (
                    <AfasQuestion1Mask message={question1MaskMessage}>
                        <AfasExtraPanel
                            values={extraDraft}
                            optionsByField={fieldOptions}
                            onChange={question1Blocked ? undefined : setExtraDraft}
                        />
                    </AfasQuestion1Mask>
                );
            case AFAS_SUB_TAB_KEYS.OPMERKING_MEMO:
                return (
                    <AfasOpmerkingMemoPanel
                        memo={memo}
                        onSave={handleSaveMemo}
                    />
                );
            default:
                return null;
        }
    };

    const renderVerzoekFields = () => {
        if (!selectedSubstitutionId) {
            return (
                <div className="afas-openstaand__placeholder card shadow-sm text-center text-muted p-4">
                    {listLoading
                        ? <Loader
                            type="ball-grid-pulse"
                            className="mt-3 d-flex mx-auto justify-content-center"
                        />
                        : 'Selecteer een openstaand verzoek in de zijbalk'}
                </div>
            );
        }

        return (
            <>
                <div className="afas-openstaand__verzoek-card card shadow-sm">
                    <div className="afas-openstaand__verzoek-body  afas-openstaand__verzoek-body--left card-body p-3">
                           <h5 className="afas-openstaand__verzoek-title mb-4">Vervangingsverzoek</h5>
                        <AfasVerzoekFields
                            fields={verzoekLeftFields}
                            values={verzoekDraft}
                            onFieldChange={handleVerzoekFieldChange}
                        />
                    </div>
                </div>

                
                <div  className={[
                        'afas-openstaand__verzoek-card',
                        question1Blocked ? 'afas-openstaand__verzoek-card--masked' : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}>
                    <div className="afas-openstaand__verzoek-body card-body p-3 afas-openstaand__verzoek-body--right">
                         <h5 className="afas-openstaand__verzoek-title mb-4">Administratieve gegevens</h5>
                         <AfasQuestion1Mask message={question1MaskMessage}>
                        <AfasVerzoekFields
                            fields={verzoekRightFields}
                            values={verzoekDraft}
                            onFieldChange={
                                question1Blocked ? undefined : handleVerzoekFieldChange
                            }
                        />
                        </AfasQuestion1Mask>
                      
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className="afas-openstaand afas-openstaand--toolbar-in-footer">
            <div className="afas-openstaand__layout">
                <div className="afas-openstaand__main">
                    <div className="afas-openstaand__verzoek-row">
                        {renderVerzoekFields()}
                    </div>

                    <div className="afas-openstaand__detail-card card shadow-sm">
                        <div className="afas-openstaand__detail-tabs card-header bg-transparent border-bottom px-3 py-2">
                            <AfasSubTabBar
                                activeSubTab={activeSubTab}
                                onSubTabChange={setActiveSubTab}
                                selectedWeek={selectedWeek}
                            />
                        </div>
                        <div className="afas-openstaand__detail-content card-body p-3">
                            {renderSubTabContent()}
                        </div>
                    </div>
                </div>

                <AfasFilterSidebar
                    listMode={listMode}
                    onListModeChange={setListMode}
                    dagtypeFilter={dagtypeFilter}
                    onDagtypeFilterChange={setDagtypeFilter}
                    dagtypeOptions={dagtypeOptions}
                    rows={sidebarRows}
                    isLoading={listLoading}
                    selectedSubstitutionId={selectedSubstitutionId}
                    selectedSubstitutorAdminId={selectedSubstitutorAdminId}
                    selectedProlongedId={selectedProlongedId}
                    onSelectPerson={handleSelectPerson}
                />
            </div>

            <AfasFooterActions
                submitMode={submitMode}
                listMode={listMode}
                onSubmitModeChange={setSubmitMode}
                onVerwerken={handleVerwerken}
                disableAutoSubmit={forceHandmatig}
                disabled={
                    isSubmitting ||
                    (!selectedProlongedId &&
                        (!selectedSubstitutionId || !selectedSubstitutorAdminId)) ||
                    (submitMode === 'auto' && !verzendenCheck.ok)
                }
                isSubmitting={isSubmitting}
            >
                <AfasListToolbar
                    compact
                    showWeek
                    listMode={listMode}
                    selectedWeek={selectedWeek}
                    onWeekChange={setSelectedWeek}
                />
            </AfasFooterActions>

            <AfasVerzendenValidationModal
                isOpen={Boolean(verzendenValidationMessage)}
                message={verzendenValidationMessage}
                onClose={() => setVerzendenValidationMessage(null)}
            />
        </div>
    );
};

export default Openstaand;
